import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react'

import { AuthSession, AuthState } from './AuthSession'
import { borrowerPublicApiClient } from './BorrowerPublicApiClient'

/**
 * The browser's one auth-service session (ADR-017 / TED-4200), replacing the `OktaAuth` instance that used
 * to be constructed in `App`.
 *
 * A module singleton rather than something the provider owns, because `borrowerApiClient` needs the token
 * getter outside of React — the previous code reached it by calling `useOktaAuth()` from a default argument,
 * which only worked because it happened to be evaluated during render.
 *
 * It talks to customer-portal-api's pass-through (`/api/v1/auth/*`), never to auth-service directly:
 * auth-service has no ingress on its application port (TED-4489), so cpa is the only front door.
 */
export const authSession = new AuthSession(borrowerPublicApiClient())

export interface AuthServiceValue {
  authState: AuthState
  /** Rejects with `SignInError`, whose `reason` distinguishes bad credentials from an outage. */
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  /** A currently-valid access token, refreshing first if the held one is spent. */
  getOrRefreshToken: () => Promise<string>
}

const AuthServiceContext = createContext<AuthServiceValue | null>(null)

export const AuthServiceProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [authState, setAuthState] = useState<AuthState>(() => authSession.currentState())

  useEffect(() => {
    const unsubscribe = authSession.subscribe(setAuthState)
    // Settle 'unknown' into a real status on first mount. A reload arrives with no access token but often a
    // live `fluro_refresh` cookie, so this is what keeps a refresh from logging the customer out.
    if (authSession.currentState().status === 'unknown') {
      authSession.restore()
    }
    return unsubscribe
  }, [])

  const signIn = useCallback((email: string, password: string) => authSession.signIn(email, password), [])
  const signOut = useCallback(() => authSession.signOut(), [])
  const getOrRefreshToken = useCallback(() => authSession.getOrRefreshToken(), [])

  return (
    <AuthServiceContext.Provider value={{ authState, signIn, signOut, getOrRefreshToken }}>
      {children}
    </AuthServiceContext.Provider>
  )
}

function useAuthServiceContext(): AuthServiceValue {
  const value = useContext(AuthServiceContext)
  if (!value) {
    throw new Error('useAuthService must be used inside an AuthServiceProvider')
  }
  return value
}

/**
 * Indirection kept for the Cypress component tests, which mount a component in
 * isolation, with no provider above it, and stub this object's method to supply a session. See
 * `setupMockUseAuthService` in `test/mockData`.
 */
export const authServiceWrapper = {
  useAuthService: useAuthServiceContext,
}

export function useAuthService(): AuthServiceValue {
  return authServiceWrapper.useAuthService()
}
